import i18n from 'i18n'
// redux imports
import store from 'store'
import { contactSelectedClear, searchClear } from 'redux/Contacts'
import { outgoingCallSourceClear } from 'redux/Anaytics'
// module imports
import { camel2Snake, titleCase } from 'modules/Helpers'
import formatPhoneNumber, {
  isPhoneNumber,
  isStarCode,
} from 'modules/PhoneNumbers'
import eventLogger from 'modules/Logger'
import { filterContacts, findContact } from 'modules/Search'
import { entityFromHeaders } from 'modules/Sip/CustomSIP'
import { extension2parkedSlot } from 'modules/ParkingSlot'
// constant imports
import CONTACTS from 'constants/Contacts'
import SESSIONS from 'constants/Sessions'
import { preparePhoneNumberForCall } from 'n2p-ui-library/utils/phone-number/phone-number.util'

// helpers
const anonymous = new RegExp(SESSIONS.hidden.anonymous, 'i')
const cleanDisplayName = (str) =>
  titleCase((str || '').toLowerCase().split(', ')?.reverse()?.join(' '))
    .replace(/\s+/g, ' ')
    .replace(anonymous, '')

const guestContactFromNumber = (phone, details = {}) => {
  const numberToDial = preparePhoneNumberForCall(phone || '')

  return {
    avatar: '',
    phone: numberToDial,
    numberToDial,
    type: CONTACTS.names.guest,
    ...details,
  }
}

/**
 * Gets or creates the number to call in the following order
 * 1) extension
 * 2) phone
 * 3) sip credentials
 *
 * @param contact
 * @param format
 *
 * @returns {string}
 */
export const numberFromContact = (contact) => {
  if (!contact) {
    eventLogger.error('invalid_contact')
    throw new Error('invalid contact to call')
  }

  const { extension, phone, numberToDial } = contact || {}

  if (numberToDial) {
    return numberToDial
  }

  // temp fix for https://idtjira.atlassian.net/browse/N2PRCON-522
  const useExtension = contact.type !== CONTACTS.names.welcomeMenu || !phone
  if (extension && useExtension) {
    return extension
  }

  if (phone) {
    return phone
  }

  const username = contact?.sipCredentials?.[0]?.username

  if (username) {
    const { UA_URI } = store.getState().config.ua

    return `${username}@${UA_URI}`
  }

  eventLogger.error('invalid_contact', contact)
  return ''
}

export const numberFromContactSimple = (contact) => {
  if (!contact) {
    eventLogger.error('invalid_contact')
    throw new Error('invalid contact to call')
  }

  const { extension, phone, numberToDial } = contact || {}

  if (numberToDial) {
    return String(numberToDial)
  }

  // temp fix for https://idtjira.atlassian.net/browse/N2PRCON-522
  const useExtension = contact.type !== CONTACTS.names.welcomeMenu || !phone
  if (extension && useExtension) {
    return String(extension)
  }

  if (phone) {
    return String(phone)
  }

  eventLogger.error('invalid_contact', contact)
  return ''
}

export const numberOrNameFromContactSimple = (contact) => {
  const { name } = contact

  if (name) {
    return name
  }

  return numberFromContactSimple(contact)
}

export const titleFromCallDetails = (callDetails) => {
  return formatPhoneNumber(callDetails?.from?.name)
}

export const titleFromContact = (contact) => {
  if (!contact) return 'Undefined'
  const { name, phone, extension, numberToDial, wasParked } = contact

  if (name) {
    return name
  }

  if (numberToDial && !wasParked) {
    return formatPhoneNumber({ number: numberToDial })
  }

  if (phone) {
    return formatPhoneNumber({ number: phone })
  }

  if (extension) {
    return extension
  }

  const callDetailsName = titleFromCallDetails(contact.callDetails)

  if (callDetailsName) {
    return callDetailsName
  }

  return 'Unknown'
}

export const subtitleFromContact = (
  contact,
  incoming,
  checkTitleEqual = true,
) => {
  if (contact.wasParked) {
    return i18n.t('SIP_CALLS.UNPARKING', {
      slot: extension2parkedSlot(contact.numberToDial),
    })
  }

  const title = titleFromContact(contact)

  if (!incoming && checkTitleEqual) {
    const callTo = formatPhoneNumber({
      number: contact?.numberToDial || numberFromContact(contact),
    })
    if (title !== callTo) {
      return callTo
    }
    return ''
  }

  if (!contact?.callDetails) return ''

  const { name, source } = contact.callDetails.from
  const nameIsTitle = String(name).toLowerCase() === String(title).toLowerCase()
  // title may be a formatted number, hence we compare formatted name and source with title
  const sourceIsTitle = formatPhoneNumber({ number: source }) === title

  if (nameIsTitle && sourceIsTitle) return ''

  if (!name || (checkTitleEqual && nameIsTitle && !sourceIsTitle)) {
    return source
  }

  if (!source || (checkTitleEqual && sourceIsTitle && !nameIsTitle)) {
    return name
  }

  const returnName = checkTitleEqual ? name : contact?.name || name
  const formattedSource = formatPhoneNumber(source)

  return formattedSource === returnName
    ? returnName
    : `${returnName}, ${formatPhoneNumber({ number: source })}`
}

export const getContact = ({ type, name } = {}) => {
  if (!type || !name) return undefined

  const contacts = store
    .getState()
    .contacts.contacts[type].filter(
      (contact) =>
        contact.id === name ||
        contact.name.toLowerCase() === name.toLowerCase(),
    )

  if (contacts.length) return contacts[0]

  return undefined
}

export const contactFromSearch = () => {
  const { search = '' } = store.getState().contacts

  // `formatPhoneNumber` doesn't work for international numbers
  // So it's just additional clear of excess spaces
  // TODO formatPhoneNumber should work with international numbers
  let numberToDial = preparePhoneNumberForCall(search)

  const contact = findContact(numberToDial)
  // remove if is not a phone number

  if (!isPhoneNumber(search) && !isStarCode(search)) {
    numberToDial = undefined
  }

  if (contact) {
    return { ...contact, numberToDial }
  }

  return guestContactFromNumber(numberToDial)
}

export const callbackContact = (callDetails) =>
  filterContacts(preparePhoneNumberForCall(callDetails.from.source), true) ||
  guestContactFromNumber(callDetails.from.source, { callDetails })

export const contactFromIncomingEvent = (session, request) => {
  // get details from session
  const {
    uri: { user },
    display_name: displayName,
  } = session.remote_identity
  // get entity from headers
  const callTo = getContact(entityFromHeaders(request))
  // check if known contact
  const contact = filterContacts(
    callTo?.name || preparePhoneNumberForCall(user),
    true,
  )
  // is direct call
  const direct = !callTo?.type || callTo.type === CONTACTS.names.teamMember
  // from.name: (cname) ex: WIRELESS CALLER or full name if known user
  // from.source: number or extension
  // source.direct: boolean
  // source.type: camel case entity or undefined
  // source.name: entity called name or undefined
  // source.id: entity called id or undefined
  // create call details
  const callDetails = {
    from: {
      name: cleanDisplayName(displayName),
      source: user,
    },
    source: {
      direct,
      type: callTo?.type,
      name: callTo?.name,
      id: callTo?.id,
    },
  }

  return {
    ...(contact || callbackContact(callDetails)),
    callDetails,
  }
}

export const contactSource = (contact, short = false) => {
  if (contact.wasParked) {
    return i18n.t(`SIP_CALLS.${short ? 'SLOT' : 'UNPARKED'}`, {
      slot: extension2parkedSlot(contact.numberToDial),
    })
  }

  if (!contact?.callDetails) return ''

  const { direct, type, name } = contact.callDetails.source

  // direct call
  if (direct) {
    return i18n.t('SIP_CALLS.DIRECT_CALL')
  }

  // entity type mapping
  const entity = camel2Snake(type).toUpperCase()
  const entityType = i18n.t(`ENTITY.${entity}`)

  if (short) {
    const title = titleFromContact(contact)
    const from = contact.callDetails?.from?.source || ''
    return title === name ? from : name
  }

  // return entity call with name
  return `${name} ${entityType}`
}

export const clearCurrentContact = () => {
  store.dispatch(searchClear())
  store.dispatch(contactSelectedClear())
  store.dispatch(outgoingCallSourceClear())
}
